import { memo, useCallback } from "react";
import { type ProductGroup } from "~/features/search/models/models";
import { useDispatch, useSelector } from "react-redux";
import {
  selectActiveProductGroups,
  selectFullTaxonomy,
  selectProductGroupCountMap,
} from "~/features/search/redux/searchSelectors";
import LinearInfo from "~/features/configurator/Linear/components/LinearInfo";
import { selectProductGroupWithUrl } from "~/features/search/redux/actions";
import { CheckboxWithLabel } from "~/common/components/CheckboxWithLabel";
import { styled } from "~/common/mui";

const TaxonomyContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(4),
  [theme.breakpoints.up("desktop-s")]: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    "& > *": {
      gridRowEnd: "span 2",
      "&:nth-last-child(1), &:nth-last-child(2)": {
        gridRowEnd: "span 1",
      },
    },
  },
}));

const TaxonomyGroupTitle = styled("div")(({ theme }) => ({
  fontWeight: "bold",
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("desktop-s")]: {
    marginBottom: theme.spacing(2.5),
  },
}));

const TaxonomyGroupList = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  [theme.breakpoints.up("desktop-s")]: {
    gap: theme.spacing(2.5),
  },
}));

export const SATaxonomyFilter = memo(() => {
  const filteredTaxonomy = useSelector(selectFullTaxonomy);

  const productGroupCount = useSelector(selectProductGroupCountMap);
  const selectedProductGroups = useSelector(selectActiveProductGroups);

  const dispatch = useDispatch();
  const onSelectGroup = useCallback((code: string) => dispatch(selectProductGroupWithUrl(code)), []);

  if (!filteredTaxonomy[0]) {
    return null;
  }

  const taxonomy = filteredTaxonomy[0];

  return (
    <TaxonomyContainer>
      {taxonomy.children.map((subGroup, index) => (
        <TaxonomyFilterItem
          group={subGroup}
          productGroupCount={productGroupCount}
          onSelectGroup={onSelectGroup}
          selectedProductGroups={selectedProductGroups}
          key={index}
        />
      ))}
    </TaxonomyContainer>
  );
});

interface TaxonomyFilterItemProps {
  group: ProductGroup;
  productGroupCount: Dict<number>;
  onSelectGroup: (code: string) => void;
  selectedProductGroups: string[];
}

const TaxonomyFilterItem = ({
  group,
  onSelectGroup,
  productGroupCount,
  selectedProductGroups,
}: TaxonomyFilterItemProps) => {
  return (
    <div className="scha__sa__taxonomy-group">
      <TaxonomyGroupTitle>
        <span>{group.name}</span>
        <LinearInfo info={group.description} popupClassName={"scha__lc__external-content--shaft"} />
      </TaxonomyGroupTitle>
      <TaxonomyGroupList>
        {group.children.map((subSubItem) => {
          const isDisabled = !(productGroupCount[subSubItem.fullFacetUid] > 0);
          const isSelected = selectedProductGroups.includes(subSubItem.fullFacetUid);
          return (
            <CheckboxWithLabel
              key={subSubItem.fullFacetUid}
              className={"flex-align-center"}
              checked={isSelected}
              onCheck={() => onSelectGroup(subSubItem.fullFacetUid)}
              data-test-id={`SACheckbox-${subSubItem.fullFacetUid}`}
              disabled={isDisabled && !isSelected}
            >
              {subSubItem.name}
            </CheckboxWithLabel>
          );
        })}
      </TaxonomyGroupList>
    </div>
  );
};
